import type { ActingOption, Permission } from '@/shared/api'

/**
 * Пара прав ресурса — зеркало `@ActingAs` бэка (`docs/roles/02-backend-contract.md` §2):
 * `own` — своё, без модификатора; `onBehalf` — от имени клиента, с модификатором `client`;
 * `full` — полный вид менеджера без модификатора. `accepts` — какие параметры модификатора,
 * кроме `client`, ручка принимает: остальные бэк отвергает `400`, и фронт их не шлёт.
 */
export type AccessPair = {
  own?: Permission
  onBehalf?: Permission
  full?: Permission
  accepts?: readonly ActingOption[]
}

/**
 * Что можно сделать с ресурсом:
 * `own` / `full` — запрос без модификатора; `client` — с модификатором;
 * `needs-client` — право есть, но ресурсу нужен клиент (бэк ответил бы `400 CLIENT_REQUIRED`);
 * `none` — права нет (бэк ответил бы `403`).
 */
export type ActingMode = 'own' | 'client' | 'full' | 'needs-client' | 'none'

/**
 * Тот же порядок, что у `actingModeOf` бэка (`src/auth/acting.ts`): с клиентом решает право
 * «от имени»; без него — своё, затем полный вид, затем «нужен клиент».
 */
export function actingModeOf(
  pair: AccessPair,
  can: (perm: Permission) => boolean,
  hasClient: boolean,
): ActingMode {
  if (hasClient) return pair.onBehalf !== undefined && can(pair.onBehalf) ? 'client' : 'none'
  if (pair.own !== undefined && can(pair.own)) return 'own'
  if (pair.full !== undefined && can(pair.full)) return 'full'
  if (pair.onBehalf !== undefined && can(pair.onBehalf)) return 'needs-client'
  return 'none'
}
